import React, { useEffect } from "react";
import BookIcon from "src/assets/images/book.svg";
import SwitchIcon from "src/assets/images/switch.svg";
import AutoMapppedChat from "./../AutoMapppedChat";

interface FileUploadedProps {
    json_response: any;
    files: File[];
    setMappedSelectedFile: (file: File) => void;
    mappedFile?: File;
}

const FileUploaded: React.FC<FileUploadedProps> = ({ json_response, mappedFile, files, setMappedSelectedFile }) => {
    const [open, setOpen] = React.useState<boolean>(true);
    const [selectedFile, setSelectedFile] = React.useState<string>("");

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedFile(e.target.value);
        const matchedFile = files.find(f => f.name === e.target.value);
        if (matchedFile) {
            setMappedSelectedFile(matchedFile);
        }
    };

    useEffect(() => {
        if (files.length > 0) {
            setMappedSelectedFile(files[0]);
            setSelectedFile(files[0].name);
        }
    }, [files]);

   

    // Function to parse string-based response like:
    // "• Filename: user_data (3).csv\n  • Category: Pitch Deck\n ..."
    const parseStringRow = (str: string) => {
        const row: Record<string, string> = {};
        str.split("\n").forEach((line) => {
            const match = line.match(/•\s*([^:]+):\s*(.+)/);
            if (match) {
                const key = match[1].trim();
                const value = match[2].trim();
                row[key] = value;
            }
        });
        return {
            Item: row["Filename"],
            Type: row["Category"],
            "Size/Rows": row["Size/Rows"],
            Format: row["Format"],
            Status: row["Status"],
        };
    };

    // Build rows depending on response shape
    let rows: any[] = [];

    if (Array.isArray(json_response) && typeof json_response[0] === "string") {
        // ✅ New format
        rows = json_response.map(parseStringRow);
    } else if (json_response?.Filename) {
        // ✅ Old format (array fields)
        rows =
            json_response?.Filename?.map((_: string, index: number) => ({
                Item: json_response?.Filename?.[index],
                Type: json_response?.Category?.[index],
                "Size/Rows": json_response?.["Size/Rows"]?.[index],
                Format: json_response?.Format?.[index],
                Status: json_response?.["Status of Data"]?.[index],
            })) || [];
    }

    return (
        <div
            className={`h-screen rounded-lg w-full overflow-hidden transition-all duration-300 ease-in-out ${open
                ? "flex-[0_0_100%] sm:flex-[0_0_75%] lg:flex-[0_0_460px] max-w-full lg:max-w-[460px]"
                : "flex-[0_0_100%] sm:flex-[0_0_215px] max-w-full sm:max-w-[215px]"
                }`}
        >
            <div className="flex flex-col h-full bg-opacity-50">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <img src={BookIcon} alt="Book Icon" className="w-3 h-3 object-contain" />
                        <h2 className="text-lg font-semibold">File Metadata & Status</h2>
                    </div>
                    <button
                        onClick={() => setOpen(!open)}
                        className="cursor-pointer rounded border border-[#DFE3E8] p-1 hover:bg-gray-100"
                        aria-label="Toggle Panel"
                    >
                        <img src={SwitchIcon} alt="Toggle" className="w-3 h-3 object-contain" />
                    </button>
                </div>

                {/* Main Content */}
                <div className="h-[55vh] overflow-y-auto pn_scroller flex flex-col">
                    {open && (
                        <div className="mb-2">
                            <label htmlFor="file-select" className="block text-sm font-medium text-gray-700 mb-1">
                                Select file to map
                            </label>
                            <select
                                id="file-select"
                                value={selectedFile}
                                onChange={handleChange}
                                className="block w-full px-2 py-1 border rounded-md border-gray-300 shadow-sm focus:outline-none focus:ring focus:ring-indigo-200"
                            >
                                {files.map((file) => (
                                    <option key={file.name} value={file.name}>
                                        {file.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}
                    {rows.length > 0 ? (
                        <div className="flex-1 overflow-auto pn_scroller">
                            <table className="min-w-full border border-[#DBDEEB] text-xs rounded-md">
                                <thead className="bg-[#F3F3F3] text-left sticky top-0 z-10 text-[#373D3F]">
                                    <tr>
                                        <th className="p-2 border border-[#DBDEEB] font-medium text-gray-700">Item</th>
                                        <th className="p-2 border border-[#DBDEEB] font-medium text-gray-700">Type</th>
                                        <th className="p-2 border border-[#DBDEEB] font-medium text-gray-700">Size/Rows</th>
                                        <th className="p-2 border border-[#DBDEEB] font-medium text-gray-700">Format</th>
                                        <th className="p-2 border border-[#DBDEEB] font-medium text-gray-700">Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row: any, idx: number) => (
                                        <tr key={idx} className="hover:bg-gray-50">
                                            <td className="p-2 border border-[#DBDEEB]">{row.Item || "-"}</td>
                                            <td className="p-2 border border-[#DBDEEB]">{row.Type || "-"}</td>
                                            <td className="p-2 border border-[#DBDEEB]">{row["Size/Rows"] || "-"}</td>
                                            <td className="p-2 border border-[#DBDEEB]">{row.Format || "-"}</td>
                                            <td className="p-2 border border-[#DBDEEB]">{row.Status || "-"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="flex-1 flex flex-col justify-center items-center text-sm text-gray-500 px-4">
                            <p>No metadata available</p>
                        </div>
                    )}
                </div>
                {mappedFile && <AutoMapppedChat file={mappedFile} />}
            </div>
        </div>
    );
};

export default FileUploaded;
